import { useState, useEffect } from 'react';
import '@/App.css';
import { ShoppingCart, User, Music, Calendar, ShoppingBag } from 'lucide-react';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({ id: null, items: [] });
  const [showCart, setShowCart] = useState(false);
  const [showBio, setShowBio] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
    initCart();
  }, []);

  const fetchProducts = async (category = null) => {
    try {
      const url = category ? `${API}/products?category=${category}` : `${API}/products`;
      const response = await axios.get(url);
      setProducts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const initCart = async () => {
    const savedCartId = localStorage.getItem('cartId');
    if (savedCartId) {
      try {
        const response = await axios.get(`${API}/cart/${savedCartId}`);
        setCart(response.data);
      } catch (error) {
        createNewCart();
      }
    } else {
      createNewCart();
    }
  };

  const createNewCart = async () => {
    try {
      const response = await axios.post(`${API}/cart`);
      setCart(response.data);
      localStorage.setItem('cartId', response.data.id);
    } catch (error) {
      console.error('Error creating cart:', error);
    }
  };

  const addToCart = async (productId) => {
    if (!cart.id) return;
    try {
      const response = await axios.post(`${API}/cart/${cart.id}/items`, {
        product_id: productId,
        quantity: 1
      });
      setCart({ ...cart, items: response.data.items });
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const removeFromCart = async (productId) => {
    if (!cart.id) return;
    try {
      const response = await axios.delete(`${API}/cart/${cart.id}/items/${productId}`);
      setCart({ ...cart, items: response.data.items });
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  const handleCheckout = async () => {
    if (!cart.id || cart.items.length === 0) return;
    
    try {
      const response = await axios.post(`${API}/checkout`, {
        cart_id: cart.id,
        email: 'customer@example.com',
        success_url: `${window.location.origin}/success`,
        cancel_url: `${window.location.origin}/`
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      if (error.response?.data?.detail) {
        alert(error.response.data.detail);
      } else {
        alert('Checkout error. Please try again.');
      }
    }
  };

  const filterProducts = (filter) => {
    setActiveFilter(filter);
    if (filter === 'all') {
      fetchProducts();
    } else {
      fetchProducts(filter);
    }
  };

  const getCartItemCount = () => {
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    let total = 0;
    cart.items.forEach(item => {
      const product = products.find(p => p.id === item.product_id);
      if (product) {
        total += product.price * item.quantity;
      }
    });
    return total.toFixed(2);
  };

  return (
    <div className="min-h-screen bg-black text-[#D4AF37]">
      {/* Header */}
      <header className="border-b border-[#D4AF37]/20 sticky top-0 bg-black z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Navigation */}
            <nav className="flex gap-8 text-sm">
              <button 
                onClick={() => filterProducts('album')}
                className={`hover:text-white transition-colors ${activeFilter === 'album' ? 'text-white' : ''}`}
                data-testid="nav-music"
              >
                <Music className="inline w-4 h-4 mr-1" /> Music
              </button>
              <button className="hover:text-white transition-colors" data-testid="nav-tour">
                <Calendar className="inline w-4 h-4 mr-1" /> Tour
              </button>
              <button 
                onClick={() => filterProducts('merch')}
                className={`hover:text-white transition-colors ${activeFilter === 'merch' ? 'text-white' : ''}`}
                data-testid="nav-merch"
              >
                <ShoppingBag className="inline w-4 h-4 mr-1" /> Merch
              </button>
            </nav>

            {/* Logo */}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <img 
                src="https://customer-assets.emergentagent.com/job_jcrown-status/artifacts/kpd55sst_jcrown%20logo.png.PNG" 
                alt="J.Crown" 
                className="h-20 md:h-24"
                data-testid="site-logo"
              />
            </div>

            {/* Icons */}
            <div className="flex gap-6">
              <button className="hover:text-white transition-colors" data-testid="user-icon">
                <User className="w-5 h-5" />
              </button>
              <button 
                className="hover:text-white transition-colors relative" 
                onClick={() => setShowCart(!showCart)}
                data-testid="cart-icon"
              >
                <ShoppingCart className="w-5 h-5" />
                {getCartItemCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[#D4AF37] text-black text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                    {getCartItemCount()}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Biography Modal */}
      {showBio && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="bio-modal">
          <div className="absolute inset-0 bg-black/90" onClick={() => setShowBio(false)}></div>
          <div className="relative bg-black border-2 border-[#D4AF37] max-w-2xl w-full mx-4 p-8 max-h-[80vh] overflow-y-auto">
            <button 
              onClick={() => setShowBio(false)} 
              className="absolute top-4 right-4 text-[#D4AF37] hover:text-white text-2xl"
              data-testid="close-bio"
            >
              ×
            </button>
            
            <h2 className="text-3xl font-bold text-white mb-6" style={{ fontFamily: 'Georgia, serif' }}>
              J.Crown Biography
            </h2>
            
            <div className="space-y-4 text-[#D4AF37]">
              <p className="text-lg leading-relaxed">
                J.Crown is a dynamic artist known for blending Afrobeats, trap, and hip-hop into a unique sound 
                that captivates audiences worldwide. With a career spanning multiple albums and collaborations 
                with industry leaders like The Trap-A-Holics, J.Crown has established himself as a force in 
                contemporary music.
              </p>
              
              <p className="text-lg leading-relaxed">
                His breakthrough project "Jackin 4 Afrobeats Hosted By The Trap-A-Holics" showcased his ability 
                to merge cultural influences while maintaining authentic storytelling. Following up with projects 
                like "Soft Life," "Black By Design," and "I Craft Singles - 16 Slices," J.Crown continues to 
                push creative boundaries.
              </p>
              
              <p className="text-lg leading-relaxed">
                Beyond music, J.Crown has built a brand with Crown Cabal merchandise, connecting with fans 
                through fashion and lifestyle. His dedication to craft and community has earned him recognition 
                as an artist who represents excellence and authenticity.
              </p>
              
              <div className="pt-4 border-t border-[#D4AF37]/20 mt-6">
                <h3 className="text-xl font-semibold text-white mb-3">Discography Highlights</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Jackin 4 Afrobeats (Hosted By The Trap-A-Holics)</li>
                  <li>Soft Life EP</li>
                  <li>Black By Design EP</li>
                  <li>I Craft Singles - 16 Slices</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50" data-testid="cart-sidebar">
          <div className="absolute inset-0 bg-black/80" onClick={() => setShowCart(false)}></div>
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-black border-l border-[#D4AF37]/20 p-6 overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Your Cart</h2>
              <button onClick={() => setShowCart(false)} className="text-[#D4AF37] hover:text-white" data-testid="close-cart">
                ✕
              </button>
            </div>

            {cart.items.length === 0 ? (
              <p className="text-center text-[#D4AF37]/60 py-8" data-testid="empty-cart-message">Your cart is empty</p>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  {cart.items.map(item => {
                    const product = products.find(p => p.id === item.product_id);
                    if (!product) return null;
                    return (
                      <div key={item.product_id} className="flex gap-4 border-b border-[#D4AF37]/20 pb-4" data-testid={`cart-item-${product.id}`}>
                        <img src={product.image} alt={product.title} className="w-20 h-20 object-cover" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-white">{product.title}</h3>
                          <p className="text-sm">${product.price}</p>
                          <p className="text-sm">Qty: {item.quantity}</p>
                        </div>
                        <button 
                          onClick={() => removeFromCart(product.id)} 
                          className="text-red-500 hover:text-red-400"
                          data-testid={`remove-item-${product.id}`}
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-[#D4AF37]/20 pt-4">
                  <div className="flex justify-between mb-4 text-xl font-bold">
                    <span>Total:</span>
                    <span data-testid="cart-total">${getCartTotal()}</span>
                  </div>
                  <PayPalScriptProvider options={{ clientId: "AWR6882zjt601PO8XtRPhCKN5NOSsxMEiX_MM-w0M6PEryQXZm5E8_JEETXdx1odPgndn_Sixz66moRA" }}>
                    <PayPalButtons 
                      style={{
                        layout: 'vertical',
                        color: 'gold',
                        shape: 'rect',
                        label: 'paypal'
                      }}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: cart.items.map(item => {
                            const product = products.find(p => p.id === item.product_id);
                            return {
                              amount: {
                                value: (product.price * item.quantity).toFixed(2)
                              },
                              description: product.title
                            };
                          })
                        });
                      }}
                      onApprove={(data, actions) => {
                        return actions.order.capture().then(function(details) {
                          alert('Transaction completed by ' + details.payer.name.given_name);
                          // Clear cart
                          setCart({ ...cart, items: [] });
                          setShowCart(false);
                        });
                      }}
                    />
                  </PayPalScriptProvider>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {loading ? (
          <div className="text-center py-20">Loading...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map(product => (
              <div key={product.id} className="group relative" data-testid={`product-${product.id}`}>
                {/* Product Image */}
                <div className="aspect-square bg-zinc-900 mb-4 overflow-hidden relative">
                  <img 
                    src={product.image} 
                    alt={product.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {product.exclusive && (
                    <span className="absolute top-4 right-4 bg-[#D4AF37] text-black px-3 py-1 text-xs font-semibold" data-testid="exclusive-badge">
                      Exclusive
                    </span>
                  )}
                </div>

                {/* Product Info */}
                <h3 className="text-white font-semibold mb-2" data-testid={`product-title-${product.id}`}>{product.title}</h3>
                
                {product.category === 'album' ? (
                  <div className="space-y-2">
                    <p className="text-sm mb-2">Listen Now</p>
                    <div className="flex flex-wrap gap-2">
                      {product.streaming_links?.apple && (
                        <a 
                          href={product.streaming_links.apple} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="px-4 py-2 border border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors text-sm"
                          data-testid={`apple-music-${product.id}`}
                        >
                          Apple Music
                        </a>
                      )}
                      {product.streaming_links?.spotify && (
                        <a 
                          href={product.streaming_links.spotify} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="px-4 py-2 border border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors text-sm"
                          data-testid={`spotify-${product.id}`}
                        >
                          Spotify
                        </a>
                      )}
                      {product.streaming_links?.youtube && (
                        <a 
                          href={product.streaming_links.youtube} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="px-4 py-2 border border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors text-sm"
                          data-testid={`youtube-${product.id}`}
                        >
                          YouTube
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-lg mb-3" data-testid={`product-price-${product.id}`}>${product.price}</p>
                    <button 
                      onClick={() => addToCart(product.id)}
                      className="w-full bg-[#D4AF37] text-black py-2 rounded hover:bg-[#FFD700] transition-colors font-semibold"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      Add to Cart
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-[#D4AF37]/20 mt-20">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-white font-semibold mb-4">Shop</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => filterProducts('album')} className="hover:text-white transition-colors">Music</button></li>
                <li><button onClick={() => filterProducts('merch')} className="hover:text-white transition-colors">Merchandise</button></li>
                <li><a href="#" className="hover:text-white transition-colors">Gift Cards</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">About</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => setShowBio(true)} className="hover:text-white transition-colors" data-testid="bio-link">Biography</button></li>
                <li><a href="#" className="hover:text-white transition-colors">Press</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Shipping & Returns</a></li>
                <li><a href="#" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Order Tracking</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Follow</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Instagram</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Twitter</a></li>
                <li><a href="#" className="hover:text-white transition-colors">YouTube</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Spotify</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#D4AF37]/20 pt-8 text-center text-sm">
            <p>&copy; 2025 J.Crown Music. All rights reserved.</p>
            <div className="flex justify-center gap-6 mt-4">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Accessibility</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;